// src/components/Common/Avatar.js
import React from 'react';

/**
 * Avatar component that displays user initials when no profile picture is available
 * @param {string} src - Profile picture URL
 * @param {string} name - User's full name
 * @param {string} size - Size of avatar ('sm', 'md', 'lg', 'xl') 
 * @param {string} className - Additional CSS classes
 */
const Avatar = ({ src, name = '', size = 'md', className = '' }) => {
  // Generate initials from name
  const getInitials = (fullName) => {
    if (!fullName || fullName.trim() === '') return 'UK'; // Unknown User
    
    const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) return 'UK';
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    
    // Take first letter of first name and first letter of last name
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  };

  // Size configurations
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
    '2xl': 'w-20 h-20 text-xl',
    '3xl': 'w-32 h-32 text-2xl'
  };

  // Generate background color based on name
  const getBackgroundColor = (fullName) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      'bg-orange-500', 'bg-cyan-500', 'bg-lime-500', 'bg-rose-500'
    ];
    
    // Use name length and first character to determine color
    const index = (fullName.length + fullName.charCodeAt(0)) % colors.length;
    return colors[index];
  };

  const initials = getInitials(name);
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const bgColor = getBackgroundColor(name);

  // Check if image src is valid and not empty
  const hasValidImage = src && src.trim() !== '' && src !== 'undefined' && src !== 'null';

  if (hasValidImage) {
    return (
      <img
        src={src}
        alt={name || 'User Avatar'}
        className={`${sizeClass} rounded-full object-cover ${className}`}
        onError={(e) => {
          // If image fails to load, hide it and show initials instead
          e.target.style.display = 'none';
          const initialsDiv = e.target.nextSibling;
          if (initialsDiv) {
            initialsDiv.style.display = 'flex';
          }
        }}
      />
    );
  }

  // Show initials avatar
  return (
    <div
      className={`${sizeClass} rounded-full ${bgColor} flex items-center justify-center text-white font-semibold ${className}`}
      title={name || 'User'}
    >
      {initials}
    </div>
  );
};

export default Avatar;